import React, { useState, useMemo, useEffect } from 'react';
import type { DataRecord, SocialOverviewPayload } from '../../../types';
import { publishSocialReply, suggestSocialReply } from '../../../api';
import { getClientBrandMeta } from '../socialBrand';
import {
  CHAN_LABEL,
  CHAN_SHORT,
  aspectLabel,
  channelOf,
  cleanReviewText,
  fmtDay,
  fmtInt,
  str,
  toneOf,
  truthy,
  type BandejaFocus,
} from './resumenData';
import type { SandboxTheme } from './SandboxResumen';
import { SlidersHorizontal } from 'lucide-react';
import { MobileFab, MobileScreen, MobileSheet, SheetOption, useIsMobile } from '../mobile/MobileChrome';
import './sandboxBandeja.css';

/** En escritorio muestra el detalle junto a la lista; en teléfono lo abre a pantalla completa. */
function DetailHost({
  mobile,
  open,
  theme,
  onBack,
  children,
}: {
  mobile: boolean;
  open: boolean;
  theme: SandboxTheme;
  onBack: () => void;
  children: React.ReactNode;
}) {
  if (!mobile) return <>{children}</>;
  return open ? (
    <MobileScreen title='Caso' theme={theme} onBack={onBack}>
      {children}
    </MobileScreen>
  ) : null;
}

export type BandejaStatusTab = 'criticos' | 'pendientes' | 'respondidos';

/** Canal que se está trabajando: las reseñas de Google se responden aquí, Meta reúne Instagram y Facebook. */
type BandejaGroup = 'google' | 'meta' | 'youtube';
type SortKey = 'antiguos' | 'recientes' | 'menos_estrellas' | 'mas_estrellas';

const SORT_LABEL: Record<SortKey, string> = {
  antiguos: 'Más antiguos primero',
  recientes: 'Más recientes primero',
  menos_estrellas: 'Menos estrellas primero',
  mas_estrellas: 'Más estrellas primero',
};
const SORTS_GOOGLE: SortKey[] = ['antiguos', 'menos_estrellas', 'mas_estrellas', 'recientes'];
const SORTS_META: SortKey[] = ['antiguos', 'recientes'];

interface Props {
  clientSlug: string;
  comments: DataRecord[];
  allComments: DataRecord[];
  overview: SocialOverviewPayload | null;
  loading: boolean;
  theme: SandboxTheme;
  onNavigateToResumen: () => void;
  onNavigateSection?: (sectionId: string) => void;
  urgentCount: number;
  showHeader?: boolean;
  /** Local, tema o reseña con que debe abrir la Bandeja. */
  focus?: BandejaFocus;
  /** Abre la vista Locales. Si no viene, el enlace baja a la sección de Locales del Resumen. */
  onNavigateToLocales?: () => void;
  /** Abre la vista Temas, con un tema elegido si viene. */
  onNavigateToTemas?: (topic?: string) => void;
}

function groupOf(c: DataRecord): BandejaGroup {
  const canal = channelOf(c);
  return canal === 'gbp' ? 'google' : canal === 'youtube' ? 'youtube' : 'meta';
}

/** Crítico: lo marca el servidor con la política de respuesta. Mismo criterio del Resumen. */
function isCriticalCase(c: DataRecord): boolean {
  return truthy(c.critico);
}

/** Canal con el que abre la Bandeja: el que tiene críticos; si ninguno los tiene, Google si el cliente tiene reseñas. */
function grupoInicial(comments: DataRecord[], hayFoco: boolean): BandejaGroup {
  const criticos = { google: 0, meta: 0, youtube: 0 };
  for (const c of comments) if (!truthy(c.has_reply) && isCriticalCase(c)) criticos[groupOf(c)] += 1;
  if (criticos.google > 0) return 'google';
  if (criticos.meta > 0) return 'meta';
  if (criticos.youtube > 0) return 'youtube';
  return hayFoco || comments.length === 0 || comments.some((c) => groupOf(c) === 'google') ? 'google' : 'meta';
}

function placeOf(c: DataRecord): string {
  return str(c.post_title).trim() || str(c.page_id).trim();
}

function topicKeyOf(c: DataRecord): string {
  return str(c.aspecto).trim().toLowerCase();
}

function isRealTopic(k: string): boolean {
  return Boolean(k) && k !== 'otro' && k !== 'null';
}

function commentTime(c: DataRecord): number {
  const s = str(c.created_time || c.date);
  if (!s) return 0;
  const t = new Date(s.includes('T') ? s : `${s}T12:00:00Z`).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function commentAge(c: DataRecord): { hours: number; label: string; isOverdue: boolean } {
  const dateStr = str(c.created_time || c.date);
  if (!dateStr) return { hours: 0, label: '0 h', isOverdue: false };
  const rawIso = dateStr.includes('T') ? dateStr : `${dateStr}T12:00:00Z`;
  const t = new Date(rawIso).getTime();
  if (isNaN(t)) return { hours: 0, label: '0 h', isOverdue: false };
  const diffHours = Math.max(0, Math.round((Date.now() - t) / 3600000));
  return {
    hours: diffHours,
    label: `${diffHours} h`,
    isOverdue: diffHours > 24,
  };
}

function commentKind(c: DataRecord): { label: string; cls: string } {
  const act = str(c.acto_de_habla).trim().toLowerCase();

  if (truthy(c.critico)) return { label: 'Crítico', cls: 'crit' };
  if (truthy(c.negativo_pendiente)) return { label: 'Negativo sin responder', cls: 'crit' };
  if (act === 'pregunta' || act === 'solicitud_contacto') return { label: 'Consulta', cls: 'query' };
  if (toneOf(c) === 'pos' || Number(c.star_rating) >= 4) return { label: 'Elogio', cls: 'praise' };
  if (act === 'reclamo') return { label: 'Reclamo', cls: 'crit' };
  return { label: 'General', cls: '' };
}

export function SandboxBandeja({
  clientSlug,
  comments,
  allComments,
  overview,
  loading: _loading,
  theme,
  onNavigateToResumen,
  onNavigateSection,
  urgentCount,
  showHeader = true,
  focus,
  onNavigateToLocales,
  onNavigateToTemas,
}: Props) {
  const isMobile = useIsMobile();
  const [detailOpen, setDetailOpen] = useState(Boolean(focus?.feedbackId));
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Abre en el canal que tiene críticos.
  const [group, setGroup] = useState<BandejaGroup>(() => focus?.group ?? grupoInicial(comments, Boolean(focus)));
  // Al llegar desde un tema se ven todos los pendientes de ese tema, no solo los críticos.
  const [onlyCritical, setOnlyCritical] = useState<boolean>(!focus?.topic && !focus?.todos);
  const [sortBy, setSortBy] = useState<SortKey>('antiguos');
  // Red solo aplica a Meta. Local solo aplica a Google. El origen lo decide el selector de Social.
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>(focus?.location ?? 'all');
  const [topicFilter, setTopicFilter] = useState<string>(focus?.topic ?? 'all');
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(focus?.feedbackId ?? null);
  const [tone, setTone] = useState<'empatico' | 'formal' | 'breve'>('empatico');
  const [draftText, setDraftText] = useState<string>('');
  const [publishing, setPublishing] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Sin la autorización del servidor, la sección de respuesta queda en gris.
  const puedeResponder = overview?.can_reply === true;
  const sortOptions = group === 'google' ? SORTS_GOOGLE : SORTS_META;

  // Locales distintos de Google Business Profile (sucursales / centros del cliente)
  const gbpLocations = useMemo(() => {
    const set = new Set<string>();
    for (const c of allComments) {
      if (channelOf(c) === 'gbp') {
        const place = placeOf(c);
        if (place) set.add(place);
      }
    }
    return Array.from(set).sort();
  }, [allComments]);

  // Casos sin respuesta registrada. Los respondidos salen de la Bandeja hasta que la sincronización de respuestas sea confiable.
  const pending = useMemo(() => comments.filter((c) => !truthy(c.has_reply)), [comments]);

  // Críticos por canal, con el mismo criterio del Resumen
  const counts = useMemo(() => {
    const out = { google: 0, meta: 0, youtube: 0 };
    for (const c of pending) {
      if (isCriticalCase(c)) out[groupOf(c)] += 1;
    }
    return out;
  }, [pending]);

  const hayYoutube = useMemo(() => comments.some((c) => groupOf(c) === 'youtube'), [comments]);

  const inGroup = useMemo(() => pending.filter((c) => groupOf(c) === group), [pending, group]);
  const base = useMemo(() => (onlyCritical ? inGroup.filter(isCriticalCase) : inGroup), [inGroup, onlyCritical]);

  // Local (Google) o red (Meta)
  const scoped = useMemo(
    () =>
      base.filter((c) => {
        if (group === 'google') {
          if (locationFilter !== 'all' && placeOf(c).toLowerCase() !== locationFilter.toLowerCase()) return false;
          return true;
        }
        if (group === 'youtube') return true;
        return channelFilter === 'all' || channelOf(c) === channelFilter;
      }),
    [base, group, locationFilter, channelFilter],
  );

  // Casos por local con el filtro de críticos aplicado
  const localCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of base) {
      const k = placeOf(c);
      if (k) m.set(k.toLowerCase(), (m.get(k.toLowerCase()) ?? 0) + 1);
    }
    return m;
  }, [base]);

  // Casos por tema con los demás filtros aplicados
  const topicCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of scoped) {
      const k = topicKeyOf(c);
      if (isRealTopic(k)) m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [scoped]);

  // Todos los temas del canal, para que el menú no cambie de forma. Los que no tienen casos van deshabilitados.
  const topicOptions = useMemo(() => {
    const keys = new Set<string>();
    for (const c of inGroup) {
      const k = topicKeyOf(c);
      if (isRealTopic(k)) keys.add(k);
    }
    if (topicFilter !== 'all') keys.add(topicFilter);
    return Array.from(keys).sort();
  }, [inGroup, topicFilter]);

  // Lista final: tema y orden
  const filteredComments = useMemo(() => {
    const list = topicFilter === 'all' ? scoped.slice() : scoped.filter((c) => topicKeyOf(c) === topicFilter);
    list.sort((a, b) => {
      if (sortBy === 'recientes') return commentTime(b) - commentTime(a);
      if (sortBy === 'menos_estrellas' || sortBy === 'mas_estrellas') {
        const sa = Number(a.star_rating) || 0;
        const sb = Number(b.star_rating) || 0;
        if (sa !== sb) {
          // Sin estrellas siempre al final
          if (sa === 0) return 1;
          if (sb === 0) return -1;
          return sortBy === 'menos_estrellas' ? sa - sb : sb - sa;
        }
      }
      return commentTime(a) - commentTime(b);
    });
    return list;
  }, [scoped, topicFilter, sortBy]);

  // Selección activa por defecto al cambiar la lista
  useEffect(() => {
    if (filteredComments.length > 0) {
      const exists = filteredComments.some((c) => str(c.feedback_id) === selectedFeedbackId);
      if (!exists) {
        setSelectedFeedbackId(str(filteredComments[0].feedback_id));
      }
    } else {
      setSelectedFeedbackId(null);
    }
  }, [filteredComments, selectedFeedbackId]);

  // Comentario seleccionado
  const selectedComment = useMemo(() => {
    if (!selectedFeedbackId) return filteredComments[0] || null;
    return filteredComments.find((c) => str(c.feedback_id) === selectedFeedbackId) || filteredComments[0] || null;
  }, [filteredComments, selectedFeedbackId]);

  const switchGroup = (g: BandejaGroup) => {
    if (g === group) return;
    setGroup(g);
    setLocationFilter('all');
    setChannelFilter('all');
    setTopicFilter('all');
    if (g === 'meta' && (sortBy === 'menos_estrellas' || sortBy === 'mas_estrellas')) setSortBy('antiguos');
    setDetailOpen(false);
  };

  const clearFilters = () => {
    setOnlyCritical(true);
    setSortBy('antiguos');
    setLocationFilter('all');
    setChannelFilter('all');
    setTopicFilter('all');
  };

  const filtersActive =
    !onlyCritical ||
    sortBy !== 'antiguos' ||
    locationFilter !== 'all' ||
    channelFilter !== 'all' ||
    topicFilter !== 'all';

  // Generar borrador de respuesta al seleccionar comentario o cambiar tono
  useEffect(() => {
    if (!selectedComment) {
      setDraftText('');
      return;
    }
    const author = str(selectedComment.author_name).trim() || 'estimado/a cliente';
    const place = str(selectedComment.post_title).trim() || 'nuestro local';
    const topicKey = topicKeyOf(selectedComment);
    const topic = topicKey ? aspectLabel(topicKey).toLowerCase() : 'la atención';
    const brand = getClientBrandMeta(clientSlug);
    const brandName = brand.displayName || (overview?.client_slug ? overview.client_slug.toUpperCase() : 'nuestro equipo');
    const contactEmail = clientSlug === 'wolf' ? 'contacto@wolfagency.cl' : `contacto@${clientSlug}.cl`;

    if (tone === 'empatico') {
      setDraftText(
        `Hola ${author}, lamentamos mucho la mala experiencia vivida en ${place} con respecto a ${topic}. No es el estándar que queremos darte. Ya derivamos tu caso a la jefatura directa para revisar lo ocurrido. Si nos escribes a ${contactEmail} con tus datos, te asistimos de forma prioritaria. Equipo ${brandName}`,
      );
    } else if (tone === 'formal') {
      setDraftText(
        `Estimado/a ${author}: Acusamos recibo de su comentario respecto a ${topic} en ${place}. Lamentamos los inconvenientes ocasionados. Su caso ha sido remitido a la administración correspondiente para su seguimiento formal. Atentamente, Equipo ${brandName}`,
      );
    } else {
      setDraftText(
        `Hola ${author}, lamentamos lo ocurrido con ${topic}. Por favor contáctanos directamente a ${contactEmail} para resolver tu situación a la brevedad. Equipo ${brandName}`,
      );
    }
  }, [selectedComment, tone, overview?.client_slug, clientSlug]);

  // Diagnósticos de la tarjeta derecha
  const diagnostics = useMemo(() => {
    if (!selectedComment) return null;
    const aspect = topicKeyOf(selectedComment);
    const sameAspectCount = aspect ? comments.filter((c) => topicKeyOf(c) === aspect).length : 0;
    const authorName = str(selectedComment.author_name).trim().toLowerCase();
    const authorPriorNegatives = authorName
      ? allComments.filter((c) => str(c.author_name).trim().toLowerCase() === authorName && toneOf(c) === 'neg').length
      : 0;

    const stars = Number(selectedComment.star_rating);
    const whyCritical =
      stars === 1
        ? '1 ★ y tema en alza'
        : stars === 2
        ? '2 ★ calificación baja'
        : toneOf(selectedComment) === 'neg'
        ? 'Queja sin responder'
        : 'Consulta abierta';

    return {
      topic: aspect ? aspectLabel(aspect) : 'General',
      critical: isCriticalCase(selectedComment),
      whyCritical,
      sameAspectCount: sameAspectCount > 0 ? `${sameAspectCount} este mes` : 'Sin otros casos',
      authorHistory:
        authorPriorNegatives > 1 ? `${authorPriorNegatives}.ª reseña negativa` : '1.ª reseña registrada',
    };
  }, [selectedComment, comments, allComments]);

  // Acciones: Publicar, Copiar, Escalar
  const handlePublish = async () => {
    if (!selectedComment) return;
    setPublishing(true);
    setNotice(null);
    try {
      await publishSocialReply({
        client_slug: clientSlug,
        feedback_id: str(selectedComment.feedback_id),
        approved_text: draftText,
        action_type: 'direct_api',
      });
      setNotice('Respuesta publicada con éxito vía API oficial.');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_err) {
      setNotice('No fue posible publicar directamente vía API. Puedes copiar la respuesta.');
    } finally {
      setPublishing(false);
    }
  };

  const handleCopy = () => {
    if (!draftText) return;
    navigator.clipboard.writeText(draftText);
    setNotice('Respuesta copiada al portapapeles y registrada como respondida.');
    if (selectedComment) {
      publishSocialReply({
        client_slug: clientSlug,
        feedback_id: str(selectedComment.feedback_id),
        approved_text: draftText,
        action_type: 'manual_copied',
      }).catch(() => {});
    }
  };

  const groupName = group === 'google' ? 'Google My Business' : group === 'youtube' ? 'YouTube' : 'Meta';
  const caseWord = filteredComments.length === 1 ? 'caso' : 'casos';

  const sortSelect = (
    <label>
      Ordenar
      <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortKey)}>
        {sortOptions.map((k) => (
          <option key={k} value={k}>
            {SORT_LABEL[k]}
          </option>
        ))}
      </select>
    </label>
  );

  const topicSelect = (
    <label>
      Tema
      <select value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)}>
        <option value='all'>Todos</option>
        {topicOptions.map((k) => {
          const n = topicCounts.get(k) ?? 0;
          return (
            <option key={k} value={k} disabled={n === 0 && k !== topicFilter}>
              {aspectLabel(k)} ({n})
            </option>
          );
        })}
      </select>
    </label>
  );

  return (
    <div className='sbx' data-theme={theme}>
      {/* Top Header sticky */}
      {showHeader && (
        <header className='sbx-top-bar'>
          <div className='sbx-top-bar-inner'>
            <div className='sbx-brand'>
              <span className='sbx-brand-title'>Social Wolf</span>
            </div>

            <nav className='sbx-nav' aria-label='Secciones'>
              <button type='button' className='sbx-nav-link' onClick={onNavigateToResumen}>
                Resumen
              </button>
              <button type='button' className='sbx-nav-link active'>
                Bandeja {urgentCount > 0 && <span className='sbx-nav-badge'>{urgentCount}</span>}
              </button>
              <button
                type='button'
                className='sbx-nav-link'
                onClick={() => {
                  onNavigateToResumen();
                  onNavigateSection?.('canales');
                }}
              >
                Canales
              </button>
              <button
                type='button'
                className='sbx-nav-link'
                onClick={() => {
                  if (onNavigateToTemas) {
                    onNavigateToTemas();
                    return;
                  }
                  onNavigateToResumen();
                  onNavigateSection?.('temas');
                }}
              >
                Temas
              </button>
              <button
                type='button'
                className='sbx-nav-link'
                onClick={() => {
                  if (onNavigateToLocales) {
                    onNavigateToLocales();
                    return;
                  }
                  onNavigateToResumen();
                  onNavigateSection?.('locales');
                }}
              >
                Locales
              </button>
              <button
                type='button'
                className='sbx-nav-link'
                onClick={() => {
                  onNavigateToResumen();
                  onNavigateSection?.('plan');
                }}
              >
                Plan
              </button>
            </nav>

            <div className='sbx-top-right' style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button type='button' className='sbx-btn-pdf' onClick={() => window.print()}>
                Exportar PDF
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Main Bandeja Content */}
      <main style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!isMobile && (
          <h1 style={{ margin: 0, fontFamily: `'Source Serif 4', Georgia, serif`, fontSize: 32, fontWeight: 700 }}>
            Bandeja de respuesta
          </h1>
        )}

        {/* Submenú por canal */}
        <div className='sbx-sub' role='tablist' aria-label='Canal'>
          <button
            type='button'
            role='tab'
            aria-selected={group === 'google'}
            className='sbx-sub-tab'
            onClick={() => switchGroup('google')}
          >
            Google My Business
            <span className={`sbx-sub-n${counts.google > 0 ? ' crit' : ''}`}>
              {counts.google} {counts.google === 1 ? 'crítico' : 'críticos'}
            </span>
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={group === 'meta'}
            className='sbx-sub-tab'
            onClick={() => switchGroup('meta')}
          >
            {isMobile ? 'Meta' : 'Meta: Instagram y Facebook'}
            <span className={`sbx-sub-n${counts.meta > 0 ? ' crit' : ''}`}>
              {counts.meta} {counts.meta === 1 ? 'crítico' : 'críticos'}
            </span>
          </button>
          {hayYoutube && (
            <button
              type='button'
              role='tab'
              aria-selected={group === 'youtube'}
              className='sbx-sub-tab'
              onClick={() => switchGroup('youtube')}
            >
              YouTube
              <span className={`sbx-sub-n${counts.youtube > 0 ? ' crit' : ''}`}>
                {counts.youtube} {counts.youtube === 1 ? 'crítico' : 'críticos'}
              </span>
            </button>
          )}
        </div>

        {notice && (
          <div
            style={{
              padding: '10px 16px',
              background: 'var(--sbx-chip)',
              border: '1px solid var(--sbx-border)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--sbx-ink)',
            }}
          >
            {notice}
          </div>
        )}

        {/* Filtro rápido en teléfono; el resto vive en la hoja de filtros. */}
        {isMobile && (
          <div className='rz-chips' role='group' aria-label='Filtro rápido'>
            <button
              type='button'
              aria-pressed={onlyCritical}
              onClick={() => {
                setOnlyCritical(!onlyCritical);
                setTopicFilter('all');
              }}
            >
              Solo críticos
            </button>
            <span className='rz-chipcount'>
              {filteredComments.length} {caseWord}
            </span>
          </div>
        )}

        {/* Filtros del canal (escritorio). En teléfono viven en la hoja de filtros. */}
        {!isMobile && (
          <div className='sbx-fbar'>
            <label className='sbx-fbar-switch'>
              <input
                type='checkbox'
                checked={onlyCritical}
                onChange={(e) => {
                  setOnlyCritical(e.target.checked);
                  setTopicFilter('all');
                }}
              />
              Solo críticos
            </label>
            {sortSelect}
            {group === 'google' ? (
              <label>
                Local
                <select
                  value={locationFilter}
                  onChange={(e) => {
                    setLocationFilter(e.target.value);
                    setTopicFilter('all');
                  }}
                >
                  <option value='all'>Todos</option>
                  {gbpLocations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc} ({localCounts.get(loc.toLowerCase()) ?? 0})
                    </option>
                  ))}
                </select>
              </label>
            ) : group === 'meta' ? (
              <label>
                Red
                <select
                  value={channelFilter}
                  onChange={(e) => {
                    setChannelFilter(e.target.value);
                    setTopicFilter('all');
                  }}
                >
                  <option value='all'>Instagram y Facebook</option>
                  <option value='meta_ig'>Instagram</option>
                  <option value='meta_fb'>Facebook</option>
                </select>
              </label>
            ) : null}
            {topicSelect}
            <span className='sbx-fbar-count'>
              {filteredComments.length} {caseWord}
            </span>
          </div>
        )}

        {/* Master - Detail Grid */}
        <div className='sbx-bandeja-layout'>
          {/* Left Column: List */}
          <section className='sbx-inbox-list' aria-label='Lista de comentarios'>
            <div className='sbx-inbox-items'>
              {filteredComments.length === 0 ? (
                <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--sbx-muted)', fontSize: '14px' }}>
                  {base.length === 0 && onlyCritical
                    ? `No hay casos críticos sin responder en ${groupName}.`
                    : 'No hay casos con estos filtros. Prueba apagar «Solo críticos» o cambiar de tema.'}
                </div>
              ) : (
                filteredComments.map((c) => {
                  const fid = str(c.feedback_id);
                  const isSelected = selectedComment && str(selectedComment.feedback_id) === fid;
                  const age = commentAge(c);
                  const kind = commentKind(c);
                  const isCrit = kind.cls === 'crit';
                  const chan = channelOf(c);
                  const chanName = chan ? CHAN_SHORT[chan] : 'Canal';
                  const placeName = placeOf(c);
                  const stars = Number(c.star_rating);
                  const text = cleanReviewText(c.message);
                  const topicKey = topicKeyOf(c);

                  return (
                    <button
                      key={fid || `${str(c.author_name)}|${str(c.date)}`}
                      type='button'
                      data-origen={str(c.origin).toLowerCase() === 'paid' ? 'paid' : 'organic'}
                      className={`sbx-inbox-item${isSelected ? ' is-selected' : ''}${isCrit ? ' is-crit' : ''}`}
                      onClick={() => {
                        setSelectedFeedbackId(fid);
                        setDetailOpen(true);
                      }}
                    >
                      <div className='sbx-item-top'>
                        <div className='sbx-item-meta'>
                          <span className='sbx-item-plat'>{chanName}</span>
                          {placeName && <span className='sbx-item-place'>{placeName}</span>}
                          {stars > 0 && <span className='sbx-item-stars'>★ {stars}</span>}
                        </div>
                        <span className={`sbx-item-age${age.isOverdue ? ' urgent' : ''}`}>{age.label}</span>
                      </div>
                      <p className='sbx-item-text'>{text || 'Sin texto'}</p>
                      <div className='sbx-item-tags'>
                        <span className={`sbx-item-pill ${kind.cls}`}>{kind.label}</span>
                        {topicKey && <span className='sbx-item-pill'>{aspectLabel(topicKey)}</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </section>

          {/* Right Column: Detail & Reply */}
          <DetailHost mobile={isMobile} open={detailOpen} theme={theme} onBack={() => setDetailOpen(false)}>
            <section className='sbx-inbox-detail' aria-label='Detalle y respuesta'>
              {selectedComment ? (
                <>
                  {/* Comment Detail Card */}
                  <div className='sbx-detail-card'>
                    <div className='sbx-detail-head'>
                      <div className='sbx-detail-author'>
                        <strong>{str(selectedComment.author_name).trim() || 'Cliente'}</strong>
                        <span>
                          {channelOf(selectedComment) === 'gbp' || !channelOf(selectedComment) ? 'Reseña en' : 'Comentario en'}{' '}
                          {selectedComment.platform ? CHAN_LABEL[channelOf(selectedComment) || 'gbp'] : 'Google'}
                          {selectedComment.post_title ? ` · ${str(selectedComment.post_title)}` : ''} ·{' '}
                          {fmtDay(str(selectedComment.date))}
                          {Number(selectedComment.star_rating) > 0 ? ` · ★ ${selectedComment.star_rating} de 5` : ''}
                        </span>
                      </div>
                      {truthy(selectedComment.has_reply) ? (
                        <span className='sbx-loc-tag good'>Respondido</span>
                      ) : (
                        <span className='sbx-detail-status-pill'>
                          Sin respuesta hace {commentAge(selectedComment).label}
                          {commentAge(selectedComment).isOverdue ? ' · fuera de plazo' : ' · dentro de plazo'}
                        </span>
                      )}
                    </div>

                    <blockquote className='sbx-detail-quote'>
                      {cleanReviewText(selectedComment.message) || 'Sin mensaje de texto'}
                    </blockquote>

                    {diagnostics && (
                      <div className='sbx-detail-chips'>
                        <div className='sbx-detail-chip'>
                          <span>Tema</span>
                          <strong>{diagnostics.topic}</strong>
                        </div>
                        <div className='sbx-detail-chip'>
                          <span>{diagnostics.critical ? 'Por qué es crítico' : 'Estado'}</span>
                          <strong>{diagnostics.critical ? diagnostics.whyCritical : 'Pendiente de respuesta'}</strong>
                        </div>
                        <div className='sbx-detail-chip'>
                          <span>Casos parecidos</span>
                          <a
                            href='#temas'
                            onClick={(e) => {
                              e.preventDefault();
                              if (onNavigateToTemas) {
                                onNavigateToTemas(topicKeyOf(selectedComment) || undefined);
                                return;
                              }
                              onNavigateToResumen();
                              onNavigateSection?.('temas');
                            }}
                          >
                            {diagnostics.sameAspectCount}
                          </a>
                        </div>
                        <div className='sbx-detail-chip'>
                          <span>Cliente</span>
                          <strong>{diagnostics.authorHistory}</strong>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Response Card */}
                  <div className='sbx-detail-card'>
                    <div className='sbx-draft-head'>
                      <strong>Borrador de respuesta</strong>

                      <div className='sbx-tone-radios' role='radiogroup' aria-label='Tono'>
                        <button
                          type='button'
                          role='radio'
                          aria-checked={tone === 'empatico'}
                          className={`sbx-tone-btn${tone === 'empatico' ? ' active' : ''}`}
                          onClick={() => setTone('empatico')}
                        >
                          Empático
                        </button>
                        <button
                          type='button'
                          role='radio'
                          aria-checked={tone === 'formal'}
                          className={`sbx-tone-btn${tone === 'formal' ? ' active' : ''}`}
                          onClick={() => setTone('formal')}
                        >
                          Formal
                        </button>
                        <button
                          type='button'
                          role='radio'
                          aria-checked={tone === 'breve'}
                          className={`sbx-tone-btn${tone === 'breve' ? ' active' : ''}`}
                          onClick={() => setTone('breve')}
                        >
                          Breve
                        </button>
                      </div>
                    </div>

                    {!puedeResponder && (
                      <p className='sbx-reply-locked' role='note'>
                        Responder está inhabilitado para tu cuenta. Pide acceso a Camilo o Joaquín.
                      </p>
                    )}
                    <label className='sbx-draft-textarea'>
                      Respuesta pública
                      <textarea rows={6} value={draftText} disabled={!puedeResponder} onChange={(e) => setDraftText(e.target.value)} />
                    </label>

                    <div className='sbx-draft-actions'>
                      <button
                        type='button'
                        className='sbx-btn-publish'
                        disabled={!puedeResponder || publishing || !draftText.trim()}
                        onClick={handlePublish}
                      >
                        {!puedeResponder
                          ? 'Inhabilitado'
                          : publishing
                          ? 'Publicando...'
                          : `Aprobar y publicar en ${CHAN_SHORT[channelOf(selectedComment) || 'gbp']}`}
                      </button>
                      <button type='button' className='sbx-btn-secondary' onClick={handleCopy}>
                        Copiar
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    padding: '48px 24px',
                    textAlign: 'center',
                    background: 'var(--sbx-surface)',
                    border: '1px solid var(--sbx-border)',
                    borderRadius: '14px',
                    color: 'var(--sbx-muted)',
                  }}
                >
                  Selecciona un comentario de la lista izquierda para ver su detalle y respuesta.
                </div>
              )}
            </section>
          </DetailHost>
        </div>
      </main>

      {isMobile && (
        <>
          <MobileFab
            theme={theme}
            label='Filtros'
            badge={filtersActive ? '1+' : undefined}
            icon={<SlidersHorizontal size={20} aria-hidden='true' />}
            onClick={() => setFiltersOpen(true)}
          />
          <MobileSheet
            open={filtersOpen}
            title={`Filtros de ${groupName}`}
            theme={theme}
            onClose={() => setFiltersOpen(false)}
            footer={
              <>
                <button type='button' className='sbx-m-action is-quiet' onClick={clearFilters}>
                  Limpiar
                </button>
                <button type='button' className='sbx-m-action' onClick={() => setFiltersOpen(false)}>
                  Ver {filteredComments.length}
                </button>
              </>
            }
          >
            <div className='sbx-m-group'>Mostrar</div>
            <div role='radiogroup' aria-label='Mostrar' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <SheetOption
                label='Solo críticos'
                selected={onlyCritical}
                onClick={() => {
                  setOnlyCritical(!onlyCritical);
                  setTopicFilter('all');
                }}
              />
            </div>
            <div className='sbx-m-group'>Ordenar</div>
            <div role='radiogroup' aria-label='Ordenar' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sortOptions.map((k) => (
                <SheetOption key={k} label={SORT_LABEL[k]} selected={sortBy === k} onClick={() => setSortBy(k)} />
              ))}
            </div>
            {group === 'google' && gbpLocations.length > 0 && (
              <>
                <div className='sbx-m-group'>Local</div>
                <div role='radiogroup' aria-label='Local' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <SheetOption
                    label='Todos'
                    selected={locationFilter === 'all'}
                    onClick={() => {
                      setLocationFilter('all');
                      setTopicFilter('all');
                    }}
                  />
                  {gbpLocations.map((loc) => (
                    <SheetOption
                      key={loc}
                      label={`${loc} (${localCounts.get(loc.toLowerCase()) ?? 0})`}
                      selected={locationFilter === loc}
                      onClick={() => {
                        setLocationFilter(loc);
                        setTopicFilter('all');
                      }}
                    />
                  ))}
                </div>
              </>
            )}
            {group === 'meta' && (
              <>
                <div className='sbx-m-group'>Red</div>
                <div role='radiogroup' aria-label='Red' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    ['all', 'Instagram y Facebook'],
                    ['meta_ig', 'Instagram'],
                    ['meta_fb', 'Facebook'],
                  ].map(([id, label]) => (
                    <SheetOption
                      key={id}
                      label={label}
                      selected={channelFilter === id}
                      onClick={() => {
                        setChannelFilter(id);
                        setTopicFilter('all');
                      }}
                    />
                  ))}
                </div>
              </>
            )}
            {topicOptions.length > 0 && (
              <>
                <div className='sbx-m-group'>Tema</div>
                <div role='radiogroup' aria-label='Tema' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <SheetOption label='Todos' selected={topicFilter === 'all'} onClick={() => setTopicFilter('all')} />
                  {topicOptions
                    .filter((k) => (topicCounts.get(k) ?? 0) > 0 || k === topicFilter)
                    .map((k) => (
                      <SheetOption
                        key={k}
                        label={`${aspectLabel(k)} (${topicCounts.get(k) ?? 0})`}
                        selected={topicFilter === k}
                        onClick={() => setTopicFilter(k)}
                      />
                    ))}
                </div>
              </>
            )}
          </MobileSheet>
        </>
      )}
    </div>
  );
}
